import { useUploadFile } from "@/src/hooks/features/use-upload-file";
import {
  DeleteOrganizationConfigResponse,
  GetOrganizationConfigsResponse,
  InsertOrganizationConfigResponse,
  OrganizationConfigItem,
  UpdateOrganizationConfigResponse,
} from "@/src/models/organization.model";
import { ORGANIZATION_CONFIG_QUERY_KEYS } from "@/src/query-keys/organization.keys";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import gqlClient from "../api/graphql.client";
import {
  DELETE_ORGANIZATION_CONFIG,
  GET_ORGANIZATION_COLOR,
  GET_ORGANIZATION_CONFIGS,
  INSERT_ORGANIZATION_CONFIG,
  UPDATE_ORGANIZATION_COLOR,
  UPDATE_ORGANIZATION_CONFIG,
} from "./organization.query";

import { decodeToken } from "@/src/services/api/auth.api";

const useInvalidateOrganizationConfigs = () => {
  const queryClient = useQueryClient();
  return () =>
    queryClient.invalidateQueries({
      queryKey: ORGANIZATION_CONFIG_QUERY_KEYS.base,
    });
};

export const useGetOrganizationConfigQuery = () =>
  useQuery<OrganizationConfigItem | null>({
    queryKey: ORGANIZATION_CONFIG_QUERY_KEYS.base,
    queryFn: async () => {
      const decoded = await decodeToken();
      const currentOrgId = decoded?.org_id;

      const client = await gqlClient();
      const data = await client.request<GetOrganizationConfigsResponse>(
        GET_ORGANIZATION_CONFIGS,
        {
          filter: JSON.stringify({ OrganizationIds: currentOrgId }),
          sort: "{}",
          pageNo: 1,
          pageSize: 1,
        },
      );
      return data.getorganizationconfigs.items[0] ?? null;
    },
  });


export const useGetOrganizationColorQuery = (options?: {
  enabled?: boolean;
}) =>
  useQuery<OrganizationConfigItem | null>({
    queryKey: ORGANIZATION_CONFIG_QUERY_KEYS.color,
    queryFn: async () => {
      const decoded = await decodeToken();
      const currentOrgId = decoded?.org_id;

      const client = await gqlClient();
      const data = await client.request<GetOrganizationConfigsResponse>(
        GET_ORGANIZATION_COLOR,
        {
          filter: JSON.stringify({ OrganizationIds: currentOrgId }),
        },
      );
      return data.getorganizationconfigs.items[0] ?? null;
    },
    enabled: options?.enabled ?? true,
  });

export const useInsertOrganizationConfigMutation = () => {
  const invalidate = useInvalidateOrganizationConfigs();
  return useMutation({
    mutationFn: async (orgLogo: string) => {
      const client = await gqlClient();
      const data = await client.request<InsertOrganizationConfigResponse>(
        INSERT_ORGANIZATION_CONFIG,
        { orgLogo },
      );
      return data.insertorganizationconfig;
    },
    onSuccess: () => invalidate(),
  });
};

export const useUpdateOrganizationConfigMutation = () => {
  const invalidate = useInvalidateOrganizationConfigs();
  return useMutation({
    mutationFn: async ({ orgLogo }: { orgLogo: string }) => {
      const decoded = await decodeToken();
      const currentOrgId = decoded?.org_id;

      const client = await gqlClient();
      const data = await client.request<UpdateOrganizationConfigResponse>(
        UPDATE_ORGANIZATION_CONFIG,
        {
          filter: JSON.stringify({ OrganizationIds: currentOrgId }),
          orgLogo,
        },
      );
      return data.updateorganizationconfig;
    },
    onSuccess: () => invalidate(),
  });
};

export const useUpdateOrganizationColorMutation = () => {
  const invalidate = useInvalidateOrganizationConfigs();
  const queryClient = useQueryClient(); // Make sure to import and use queryClient
  
  return useMutation({
    mutationFn: async (primaryColor: string) => {
      const decoded = await decodeToken();
      const currentOrgId = decoded?.org_id;

      const client = await gqlClient();
      const data = await client.request<UpdateOrganizationConfigResponse>(
        UPDATE_ORGANIZATION_COLOR,
        {
          filter: JSON.stringify({ OrganizationIds: currentOrgId }),
          primaryColor,
        },
      );
      return data.updateorganizationconfig;
    },
    onSuccess: () => {
      invalidate();
      queryClient.invalidateQueries({ queryKey: ORGANIZATION_CONFIG_QUERY_KEYS.color });
    },
  });
};

export const useDeleteOrganizationConfigMutation = () => {
  const invalidate = useInvalidateOrganizationConfigs();
  return useMutation({
    mutationFn: async (organizationId: string) => {
      const client = await gqlClient();
      const data = await client.request<DeleteOrganizationConfigResponse>(
        DELETE_ORGANIZATION_CONFIG,
        { filter: JSON.stringify({ OrganizationIds: organizationId }) },
      );
      return data.deleteorganizationconfig;
    },
    onSuccess: () => invalidate(),
  });
};

// Upload org logo to Azure then save the resulting URL to the schema
export const useUploadOrgLogoMutation = () => {
  const { mutateAsync: uploadFile } = useUploadFile();
  const { mutateAsync: insertConfig } = useInsertOrganizationConfigMutation();
  const { mutateAsync: updateConfig } = useUpdateOrganizationConfigMutation();

  return useMutation({
    mutationFn: async ({
      uri,
      fileName,
      mimeType,
      existingConfigId,
    }: {
      uri: string;
      fileName?: string;
      mimeType?: string;
      existingConfigId?: string;
    }) => {
      const permanentUrl = await uploadFile({ uri, fileName, mimeType });

      if (existingConfigId) {
        return updateConfig({ orgLogo: permanentUrl });
      }

      return insertConfig(permanentUrl);
    },
  });
};
